'use client';

import Link from 'next/link';
import JsonLd from '@/components/JsonLd';
import IndustryCrossLinks from "@/components/landing/IndustryCrossLinks";
import ResearchBacking from "@/components/landing/ResearchBacking";
import Footer from "@/components/shared/Footer";
import PublicHeader from "@/components/shared/PublicHeader";

const dentalSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Aditya Labs AI Patient Coordinator for Dental Practices",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description: "AI-powered patient coordinator that fills cancelled slots automatically, texts your waitlist, and books patients 24/7.",
  url: "https://adityalabs.ai/dental",
  audience: { "@type": "BusinessAudience", audienceType: "Dental Practices" },
  provider: { "@type": "Organization", name: "Aditya Labs", url: "https://adityalabs.ai" },
  offers: { "@type": "Offer", price: "49", priceCurrency: "USD", description: "Plans from $49/month" },
};

const dentalFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is an AI receptionist for dental practices?",
      acceptedAnswer: { "@type": "Answer", text: "An AI receptionist for dental practices is a chatbot trained on your specific practice information \u2014 services, insurance plans, hours, and booking procedures \u2014 that responds to patient enquiries instantly, 24/7. It handles appointment confirmations, waitlist management, insurance questions, and new patient intake through your website chat." },
    },
    {
      "@type": "Question",
      name: "How does AI dental appointment booking work?",
      acceptedAnswer: { "@type": "Answer", text: "The AI uses retrieval-augmented generation (RAG) to answer questions from your uploaded knowledge base. When a cancellation occurs, the AI automatically contacts waitlisted patients and confirms bookings directly based on your available slots. When a new patient asks about availability, the AI checks your schedule and confirms the appointment. It only shares information you have explicitly provided." },
    },
    {
      "@type": "Question",
      name: "What are the limitations of AI agents for dental practices?",
      acceptedAnswer: { "@type": "Answer", text: "AI agents cannot provide medical diagnoses, recommend treatments, or interpret test results. They are designed for structured tasks like scheduling, answering FAQs, and collecting intake information. Complex clinical questions are always escalated to your team." },
    },
  ],
};

const dentalResearch = [
  {
    text: "The average dental practice loses $150–$400 per empty chair from last-minute cancellations and no-shows.",
    source: "Dental Economics / ADA Practice Survey 2024",
    url: "https://www.dentaleconomics.com/",
  },
  {
    text: "Dental practices that implement automated patient outreach reduce no-show rates by up to 30%.",
    source: "Journal of Dental Practice Management 2024",
  },
  {
    text: "75% of patients prefer text-based appointment confirmations and scheduling over phone calls.",
    source: "ADA Communications Trend Report 2024",
    url: "https://www.ada.org/",
  },
];

export default function DentalLandingPage() {
  return (
    <div className="min-h-screen bg-[#0B0F19] text-white font-sans overflow-x-hidden selection:bg-teal-500/30">
      <JsonLd data={dentalSchema} />
      <JsonLd data={dentalFaqSchema} />

      {/* NAVBAR */}
      <PublicHeader variant="industry" accentColor="teal" maxWidth="max-w-7xl" />

      {/* HERO SECTION */}
      <section className="relative pt-16 pb-24 lg:pt-32 lg:pb-40 overflow-hidden">

        {/* Ambient Background Glow (Teal for Clinical Trust) */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-teal-600/20 rounded-full blur-[120px] -z-10 opacity-50"></div>

        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">

          {/* LEFT COLUMN: COPY */}
          <div className="space-y-10 relative z-10">

            {/* Urgency Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-bold tracking-wider uppercase animate-pulse">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
              </span>
              Warning: You have 3 empty slots this week
            </div>

            {/* Headline */}
            <h1 className="text-5xl lg:text-7xl font-extrabold leading-tight tracking-tight">
              Empty Chairs Cost You <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">Lost Revenue Every Day.</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg lg:text-xl text-gray-400 max-w-lg leading-relaxed">
              Your front desk is closed, but your appointment book is open. Our AI fills cancelled slots automatically by texting your waitlist and booking patients 24/7.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link href="/signup" className="flex items-center justify-center bg-teal-600 hover:bg-teal-500 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-xl shadow-teal-900/40 transition-all transform hover:-translate-y-1">
                Fill My Schedule
              </Link>
              <Link href="/demo#dental-practice" className="flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 text-white px-8 py-4 rounded-xl font-medium backdrop-blur-sm transition-all">
                Watch Live Demo
              </Link>
            </div>

            {/* Trust/Guarantee */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-6 text-sm text-gray-500 dark:text-gray-400 pt-6 border-t border-gray-800/50">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                <span className="font-medium text-gray-300">PII-Protected — Designed to Stay Outside HIPAA Scope</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                <span className="font-medium text-gray-300">&ldquo;Zero Empty Chair&rdquo; Guarantee</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 max-w-md">If our AI doesn&apos;t help fill at least 5 cancelled slots in your first 30 days, we&apos;ll extend your trial for free.</p>
            </div>
          </div>

          {/* RIGHT COLUMN: VISUAL PROOF (Waitlist Automation) */}
          <div className="relative mx-auto lg:mx-0 w-full max-w-md">

            {/* Glass Container */}
            <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-3 rounded-[2.5rem] border border-gray-700/50 shadow-2xl backdrop-blur-xl relative z-10">
              <div className="bg-black rounded-[2rem] overflow-hidden border border-gray-800 h-[650px] flex flex-col relative">

                {/* Status Bar */}
                <div className="bg-gray-900/80 backdrop-blur-md p-4 flex items-center justify-between border-b border-gray-800 sticky top-0 z-20">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-xs font-bold shadow-lg">AI</div>
                    <div>
                      <div className="font-semibold text-white text-sm">Patient Coordinator</div>
                      <div className="text-[10px] text-teal-400 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse"></span>
                        Monitoring Schedule
                      </div>
                    </div>
                  </div>
                </div>

                {/* Chat Area */}
                <div className="flex-1 p-5 space-y-6 overflow-y-auto bg-gray-950 scrollbar-hide">
                  <div className="text-center text-[10px] font-medium text-gray-600 dark:text-gray-400 uppercase tracking-widest my-4">Today 9:15 AM</div>

                  {/* System Event: Cancellation */}
                  <div className="flex justify-center">
                    <div className="bg-red-500/10 text-red-400 text-xs px-3 py-1 rounded-full border border-red-500/20">
                      ⚠ Cancellation Alert: John Doe (2:00 PM Slot)
                    </div>
                  </div>

                  {/* Message 1 (AI Actions) */}
                  <div className="flex justify-start">
                    <div className="bg-gray-800 text-gray-300 px-4 py-2 rounded-xl text-xs italic border border-gray-700 w-full">
                      Scanning waitlist for 2:00 PM match... Found: Sarah Smith.
                    </div>
                  </div>

                  {/* Message 2 (AI to Patient) */}
                  <div className="flex justify-start">
                    <div className="bg-teal-600 text-white px-4 py-3 rounded-2xl rounded-tl-sm max-w-[85%] text-sm leading-relaxed shadow-lg shadow-teal-900/20">
                      Hi Sarah! A 2:00 PM slot just opened up for your cleaning with Dr. Lee. Would you like to take it? 🦷
                    </div>
                  </div>

                  {/* Message 3 (Patient) */}
                  <div className="flex justify-end">
                    <div className="bg-gray-800 text-gray-100 px-4 py-3 rounded-2xl rounded-tr-sm max-w-[85%] text-sm leading-relaxed shadow-md">
                      Yes please! I&apos;ll take it.
                    </div>
                  </div>

                  {/* Message 4 (AI Confirmation) */}
                  <div className="flex justify-start">
                    <div className="bg-teal-600 text-white px-4 py-3 rounded-2xl rounded-tl-sm max-w-[85%] text-sm leading-relaxed shadow-lg shadow-teal-900/20">
                      Perfect. You are booked for 2:00 PM today. See you soon!
                    </div>
                  </div>

                  {/* Revenue Recovered Notification */}
                  <div className="flex justify-center my-6">
                    <div className="bg-green-500/10 text-green-400 text-xs font-semibold px-4 py-1.5 rounded-full border border-green-500/20 flex items-center gap-2">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                      Slot Filled — Revenue Recovered
                    </div>
                  </div>
                </div>

                {/* Input Area (Visual) */}
                <div className="p-4 bg-gray-900 border-t border-gray-800">
                  <div className="h-12 bg-gray-800 rounded-full w-full opacity-50 border border-gray-700"></div>
                </div>
              </div>
            </div>

            {/* Back Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-teal-500/20 blur-[90px] -z-10 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Projected Impact */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Projected Impact for Dental Practices</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-teal-400 mb-2">Recover Cancelled Slots</h3>
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                AI automatically contacts your waitlist when a slot opens. Based on industry data, filling just 3 cancelled slots per month at $300 average value could recover $900+/month in lost revenue.
              </p>
              <p className="text-xs text-gray-500">Based on published dental industry appointment data</p>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-blue-400 mb-2">Reduce Front Desk Overload</h3>
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                AI handles routine questions (hours, insurance, appointment booking) so your team can focus on in-person patient care. Typical setup takes under an hour.
              </p>
              <p className="text-xs text-gray-500">We are actively onboarding our first dental practices</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-10 px-6">
        <div className="max-w-2xl mx-auto text-center bg-teal-900/30 border border-teal-700/40 rounded-2xl p-8">
          <p className="text-white font-semibold text-lg mb-2">Plans start at $49/month</p>
          <p className="text-gray-400 text-sm mb-4">One filled cancellation covers your entire monthly cost.</p>
          <Link href="/pricing" className="text-teal-400 hover:text-teal-300 text-sm font-medium underline">View all pricing plans →</Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 bg-gray-900/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[...dentalFaqSchema.mainEntity,
              { name: "Does it integrate with Dentrix or Open Dental?", acceptedAnswer: { text: "The AI works alongside any practice management system. You upload your schedule availability and the AI handles patient-facing communication. For Dentrix and Open Dental specifically, the AI collects patient details and booking requests in a structured format that your front desk can enter into your PMS in seconds. Direct PMS integration via API is on our roadmap for Q3 2026." } },
              { name: "Do you sign BAAs (Business Associate Agreements)?", acceptedAnswer: { text: "We do not currently sign BAAs. Our dental product is designed to avoid collecting Protected Health Information (PHI), keeping your practice outside the scope of HIPAA-regulated data exchange with us. All patient conversations are encrypted at rest (AES-256) and in transit (TLS 1.3). We do not use patient data to train AI models. Data is stored in SOC 2-aligned infrastructure with access controls and audit logging." } },
            ].map((faq: { name: string; acceptedAnswer: { text: string } }, i: number) => (
              <div key={i} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-3">{faq.name}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{faq.acceptedAnswer.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ResearchBacking stats={dentalResearch} />
      <IndustryCrossLinks current="dental" />
      <Footer />
    </div>
  );
}
